import React from 'react';
import parse, {
    domToReact,
    Element,
    HTMLReactParserOptions,
} from 'html-react-parser';
import DOMPurify from 'dompurify';
import { omit } from 'lodash';
import SafeLink from '@/components/extra/SafeLink/SafeLink';
import { API_BASE_URI } from '@/store';
import classNames from 'classnames';
import styleToJS from 'style-to-object';
import { SITE_ORIGIN } from '@/store';

const apiHostRegex =
    /https:\/\/((dev|stage)\.)?api\.wirwollenwiesen\.de\/files\//gi;

const VOID_ELEMENTS = ['br', 'img', 'hr', 'input', 'meta', 'link'];

export const parseText = (htmlString: string) => {
    const clean = DOMPurify.sanitize(htmlString, {
        USE_PROFILES: { html: true },
    });

    const parserOptions: HTMLReactParserOptions = {
        replace: (domNode) => {
            if (!(domNode instanceof Element)) return;

            const originalAttribs = domNode.attribs || {};

            // 1. CLEAN PROPS: Omit attributes that React handles differently or that we remap
            // This prevents "Invalid DOM property srcset" warnings by removing lowercase keys
            const props: any = {
                ...omit(originalAttribs, ['class', 'style', 'src', 'srcset']),
                className: originalAttribs.class,
                suppressHydrationWarning: true,
            };

            // 2. STYLE HANDLING
            if (originalAttribs.style) {
                const styleObj = styleToJS(originalAttribs.style);
                if (styleObj) {
                    // Omit white-space properties using Lodash to keep it lean
                    const cleanStyle = omit(styleObj, [
                        'whiteSpace',
                        'white-space',
                        'textAlign',
                        'text-align',
                    ]);
                    if (Object.keys(cleanStyle).length > 0) {
                        props.style = cleanStyle;
                    }
                }
            }

            // 3. IMAGE HANDLING (Mapped to camelCase)
            if (domNode.name === 'img') {
                props.src = originalAttribs.src?.replace(
                    apiHostRegex,
                    `${API_BASE_URI}/files/`,
                );
                props.srcSet = originalAttribs.srcset?.replace(
                    apiHostRegex,
                    `${API_BASE_URI}/files/`,
                );
                return React.createElement('img', props);
            }

            // 4. RECURSIVE CHILDREN
            const children = domToReact(domNode.children as any, parserOptions);

            // 5. LINK LOGIC
            if (domNode.name === 'a' && originalAttribs.href) {
                let isInternal = false;
                let targetPath = originalAttribs.href;

                try {
                    const parsedUrl = new URL(
                        originalAttribs.href,
                        SITE_ORIGIN,
                    );

                    // Matches wirwollenwiesen.de or any subdomain (*.wirwollenwiesen.de)
                    const matchesInternalDomain =
                        parsedUrl.hostname === 'wirwollenwiesen.de' ||
                        parsedUrl.hostname.endsWith('.wirwollenwiesen.de');

                    // If it's relative, matches the current site origin, or matches any wirwollenwiesen.de domain
                    if (
                        originalAttribs.href.startsWith('/') ||
                        parsedUrl.origin === SITE_ORIGIN ||
                        matchesInternalDomain
                    ) {
                        isInternal = true;
                        targetPath =
                            parsedUrl.pathname +
                            parsedUrl.search +
                            parsedUrl.hash;
                    }
                } catch {
                    isInternal = false;
                }

                if (isInternal) {
                    return (
                        <SafeLink
                            {...props}
                            className={classNames(
                                originalAttribs.class,
                                'Tx_lk-internal',
                            )}
                            to={targetPath}
                        >
                            {children}
                        </SafeLink>
                    );
                }

                return (
                    <a
                        {...props}
                        href={originalAttribs.href}
                        target="_blank"
                        rel="noreferrer"
                        className={classNames(
                            originalAttribs.class,
                            'Tx_lk-external',
                        )}
                    >
                        {children}
                    </a>
                );
            }

            // 6. CATCH-ALL
            const isVoid = VOID_ELEMENTS.includes(domNode.name);
            return React.createElement(
                domNode.name,
                props,
                isVoid ? undefined : children,
            );
        },
    };

    return parse(clean, parserOptions);
};
